/**
 * Three-way merge of one language's progress when two devices changed it independently.
 *
 * `base` is the last copy both sides are known to share (null if unknown). Counters are merged as
 * base + (local - base) + (remote - base), so work done on each device is kept without double counting the shared
 * history; with no base the larger value is used, which can under-count but never double counts. Collections are
 * unioned, and everything else comes from the side with the more recent activity.
 */
type J = Record<string, any>;
const isObj = (v: unknown): v is J => v !== null && typeof v === "object" && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const at = (p: J) => (isNum(p.lastActivity?.at) ? p.lastActivity.at : 0);

export function counter(base: unknown, a: number, b: number): number {
  return isNum(base) ? Math.max(0, base + (a - base) + (b - base)) : Math.max(a, b);
}

/** Merge objects whose numeric fields are counters; other fields come from `winner`. */
function mergeCounters(base: J | undefined, a: J, b: J, winner: J): J {
  const out: J = { ...b, ...a, ...winner };
  for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (isNum(a[key]) && isNum(b[key])) out[key] = counter(base?.[key], a[key], b[key]);
  }
  return out;
}

function unionBy<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Map<string, T>();
  for (const item of items) if (!seen.has(key(item))) seen.set(key(item), item);
  return [...seen.values()];
}

function mergeRows(base: J | undefined, a: J = {}, b: J = {}, winnerIsA: boolean, each: (base: J | undefined, a: J, b: J, winner: J) => J): J {
  const out: J = {};
  for (const id of new Set([...Object.keys(a), ...Object.keys(b)])) {
    const x = a[id], y = b[id];
    out[id] = x === undefined ? y : y === undefined ? x : each(base?.[id], x, y, winnerIsA ? x : y);
  }
  return out;
}

function mergeRecord(base: J | undefined, a: J, b: J): J {
  const winner = (Number(a.lastAt) || 0) >= (Number(b.lastAt) || 0) ? a : b;
  const out = mergeCounters(base, a, b, winner);
  out.fastDays = [...new Set([...(a.fastDays ?? []), ...(b.fastDays ?? [])])].sort();
  return out;
}

function mergeStoryExposure(base: J | undefined, a: J, b: J, winner: J): J {
  const out = mergeCounters(base, a, b, winner);
  out.results = unionBy([...(a.results ?? []), ...(b.results ?? [])], (r) => JSON.stringify(r))
    .sort((x, y) => (Number(x.at) || 0) - (Number(y.at) || 0)).slice(-30);
  return out;
}

export function mergeLanguage(base: J | null | undefined, local: J, remote: J): J {
  const localWins = at(local) >= at(remote);
  const [winner, other] = localWins ? [local, remote] : [remote, local];
  const out: J = { ...other, ...winner };

  out.records = {};
  for (const mode of new Set([...Object.keys(local.records ?? {}), ...Object.keys(remote.records ?? {})])) {
    out.records[mode] = mergeRows(base?.records?.[mode], local.records?.[mode], remote.records?.[mode], localWins, (b, x, y) => mergeRecord(b, x, y));
  }

  // The card reviewed more often is the newer schedule; on a tie the later due date wins.
  if (isObj(local.flashcards) || isObj(remote.flashcards)) {
    out.flashcards = mergeRows(undefined, local.flashcards, remote.flashcards, localWins, (_b, x, y) => {
      const rx = Number(x.repetitions) || 0, ry = Number(y.repetitions) || 0;
      return rx !== ry ? (rx > ry ? x : y) : (Number(x.due) || 0) >= (Number(y.due) || 0) ? x : y;
    });
  }

  out.sessions = unionBy([...(local.sessions ?? []), ...(remote.sessions ?? [])], (s) => `${s.at}|${s.type}|${s.answerMode}|${s.count}`)
    .sort((x, y) => (Number(x.at) || 0) - (Number(y.at) || 0)).slice(-100);

  out.listeningSeconds = counter(base?.listeningSeconds, Number(local.listeningSeconds) || 0, Number(remote.listeningSeconds) || 0);
  const since = [local.listeningSince, remote.listeningSince].filter((v) => isNum(v) && v > 0);
  if (since.length) out.listeningSince = Math.min(...since);

  if (isObj(local.exposure) || isObj(remote.exposure)) {
    const a = local.exposure ?? {}, b = remote.exposure ?? {}, e = base?.exposure;
    const starts = [a.since, b.since].filter(isNum);
    out.exposure = {
      ...b, ...a, ...(localWins ? a : b),
      since: starts.length ? Math.min(...starts) : Date.now(),
      words: mergeRows(e?.words, a.words, b.words, localWins, mergeCounters),
      characters: mergeRows(e?.characters, a.characters, b.characters, localWins, mergeCounters),
      stories: mergeRows(e?.stories, a.stories, b.stories, localWins, mergeStoryExposure),
    };
  }

  const lt = local.tracks ?? {}, rt = remote.tracks ?? {};
  const wt = localWins ? lt : rt;
  out.tracks = {
    ...(localWins ? rt : lt), ...wt,
    study: {
      ...(rt.study ?? {}), ...(lt.study ?? {}), ...(wt.study ?? {}),
      reviewedWordIds: [...new Set([...(wt.study?.reviewedWordIds ?? []), ...((localWins ? rt : lt).study?.reviewedWordIds ?? [])])],
    },
    audio: {
      ...(rt.audio ?? {}), ...(lt.audio ?? {}), ...(wt.audio ?? {}),
      programs: mergeRows(undefined, lt.audio?.programs, rt.audio?.programs, localWins, (_b, x, y, w) => ({ ...w, everCompleted: !!(x.everCompleted || y.everCompleted || x.completed || y.completed) })),
    },
    stories: { ...(localWins ? rt : lt).stories, ...wt.stories },
  };

  if (at(local) || at(remote)) out.lastActivity = winner.lastActivity;
  return out;
}
